import { Mail } from "lucide-react";
import { COMMUNITY_LINKS, EMAIL_HREF } from "@/data/community";

/**
 * Compact, reusable component that renders the three community contact
 * channels (WhatsApp group, WhatsApp channel, email) in a consistent way
 * across:
 *
 *   - "popover"  → tight vertical list inside the Home hero popover
 *   - "block"    → larger card grid for the /contact page
 *
 * Footer uses plain text links of the same URLs and does not import this
 * component, to keep the footer copy lightweight.
 *
 * The component is bilingual (lang prop) and direction-agnostic. RTL
 * spacing is handled via logical `gap-*` utilities — no `mr-*` / `ml-*`.
 */

const COPY = {
  ar: {
    group: {
      title: "مجموعة واتساب",
      desc: "انضم إلى الطلاب وتعلّم الذكاء الاصطناعي معنا",
    },
    channel: {
      title: "قناة واتساب",
      desc: "تابع آخر التحديثات والإعلانات",
    },
    email: {
      title: "راسلنا",
      desc: COMMUNITY_LINKS.email,
    },
  },
  en: {
    group: {
      title: "WhatsApp Group",
      desc: "Connect with students learning AI together",
    },
    channel: {
      title: "WhatsApp Channel",
      desc: "Follow updates and announcements",
    },
    email: {
      title: "Email us",
      desc: COMMUNITY_LINKS.email,
    },
  },
};

/**
 * Inline WhatsApp brand mark. Kept inline (rather than an asset) so the
 * SVG inherits `currentColor` and respects dark-mode automatically.
 */
function WhatsAppIcon({ size = 18, className = "" }) {
  return (
    <svg
      role="img"
      aria-hidden="true"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
    >
      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
    </svg>
  );
}

export { WhatsAppIcon };

/**
 * Build the channel item set in a stable order. Group first (most
 * actionable for newcomers), then channel, then email.
 */
function getItems(t) {
  return [
    {
      key: "group",
      href: COMMUNITY_LINKS.whatsappGroup,
      external: true,
      title: t.group.title,
      desc: t.group.desc,
      icon: WhatsAppIcon,
      iconColor: "#25D366",
      iconBg: "rgba(37, 211, 102, 0.12)",
    },
    {
      key: "channel",
      href: COMMUNITY_LINKS.whatsappChannel,
      external: true,
      title: t.channel.title,
      desc: t.channel.desc,
      icon: WhatsAppIcon,
      iconColor: "#25D366",
      iconBg: "rgba(37, 211, 102, 0.12)",
    },
    {
      key: "email",
      href: EMAIL_HREF,
      external: false,
      title: t.email.title,
      desc: t.email.desc,
      icon: Mail,
      iconColor: "var(--sas-accent)",
      iconBg: "var(--sas-accent-soft)",
    },
  ];
}

export default function CommunityActions({
  lang = "en",
  variant = "popover",
  testId = "community-actions",
  onItemClick,
}) {
  const t = COPY[lang] || COPY.en;
  const items = getItems(t);

  if (variant === "popover") {
    // Tight vertical list designed to live inside a small popover panel.
    return (
      <div className="flex flex-col" data-testid={testId}>
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <a
              key={item.key}
              href={item.href}
              target={item.external ? "_blank" : undefined}
              rel={item.external ? "noopener noreferrer" : undefined}
              onClick={onItemClick}
              className="
                flex items-center gap-3
                px-3 py-2.5 rounded-xl
                text-start
                transition-colors
                hover:bg-[color:var(--sas-accent-soft)]
              "
              data-testid={`${testId}-${item.key}`}
            >
              <span
                className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full"
                style={{ background: item.iconBg, color: item.iconColor }}
                aria-hidden
              >
                <Icon size={17} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[14px] font-semibold text-[color:var(--sas-ink)] leading-tight">
                  {item.title}
                </span>
                <span className="block text-[12px] text-[color:var(--sas-ink-2)] mt-0.5 truncate">
                  {item.desc}
                </span>
              </span>
            </a>
          );
        })}
      </div>
    );
  }

  // "block" — larger card grid for the /contact page. 1 col on mobile,
  // 3 cols at sm+. Mobile-first per brief: full-width tap targets.
  return (
    <div
      className="grid gap-4 sm:grid-cols-3"
      data-testid={testId}
    >
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <a
            key={item.key}
            href={item.href}
            target={item.external ? "_blank" : undefined}
            rel={item.external ? "noopener noreferrer" : undefined}
            onClick={onItemClick}
            className="
              sas-card
              flex flex-col items-start gap-3
              transition-colors
              hover:border-[color:var(--sas-accent)]
              focus-visible:border-[color:var(--sas-accent)]
              focus-visible:outline-none
            "
            data-testid={`${testId}-${item.key}`}
          >
            <span
              className="inline-flex items-center justify-center w-12 h-12 rounded-full"
              style={{ background: item.iconBg, color: item.iconColor }}
              aria-hidden
            >
              <Icon size={22} />
            </span>
            <h3 className="text-[16px] font-semibold text-[color:var(--sas-ink)] leading-tight">
              {item.title}
            </h3>
            <p className="text-[13.5px] leading-[1.6] text-[color:var(--sas-ink-2)] break-all">
              {item.desc}
            </p>
          </a>
        );
      })}
    </div>
  );
}
